import NiceModal from '@ebay/nice-modal-react';

import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import Badge from 'components/field/Badge';
import Button from 'components/field/Button';
import LordIcon from 'components/global/LordIcon';
import LottiePlayer from 'components/global/LottiePlayer';
import Skeleton from 'components/global/Skeleton';
import Typography from 'components/global/Typography';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useLocale from 'hooks/useLocale';

import NotificationDetailsModal from 'pages/Topbar/Notifications/NotificationDetailsModal';

import { NOTIFICATION } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { THEME } from 'utils/Theme';
import PATHS from 'utils/paths';

import * as S from './NotificationPopup.styled';
import { notificationType } from './Notifications';
import { setNotificationRead } from './notificationsSlice';

/**
 * @author @muhammedburakak
 */

const NotificationPopup = ({ closePopper }) => {
	const axiosPrivate = useAxiosPrivate();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const { showError } = useAlert();
	const [isLoading, setIsloading] = useState(true);
	const [notificationData, setNotificationData] = useState();
	const { getLocaleDateWithTime } = useLocale();
	const darkMode = useSelector((state) => state.layout.darkMode);

	const getNotifications = async () => {
		const requestJson = {
			paging: {
				offset: 0,
				count: 5
			},
			filter: [
				{
					action: 'isNot',
					key: ['type'],
					value: ['export']
				}
			],
			sort: {
				key: ['date'],
				order: 'desc'
			},
			totalRecord: false
		};
		try {
			const response = await axiosPrivate.post(NOTIFICATION.GET_NOTIFICATIONS, requestJson);
			setNotificationData(response.data.data);
		} catch (error) {
			AppLogger.error(error);
		}
		setIsloading(false);
	};

	const handleReadNotification = async (ID) => {
		try {
			const response = await axiosPrivate.post(NOTIFICATION.UPDATE_NOTIFICATION_READ, { ID });
			if (response.status === 200) {
				dispatch(setNotificationRead(ID));
			} else {
				AppLogger.error(response);
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
		}
	};

	useEffect(() => {
		getNotifications();
	}, []);

	return (
		<S.NotificationPopup>
			<S.Header>
				<Typography component="h1" fontWeight="500">
					notifications
				</Typography>
			</S.Header>

			<div style={{ display: 'grid' }}>
				{isLoading ? (
					<div>
						{[...Array(5)].map((_, i) => (
							<S.LoadingRow key={i}>
								<div style={{ display: 'flex' }}>
									<S.BoxIcon style={{ padding: '0' }}>
										<Skeleton width="3rem" height="3rem" borderRadius="50%" />
									</S.BoxIcon>
									<div className="px-4" style={{ width: '100%' }}>
										<Skeleton width="100%" height="3rem" />
										<Skeleton width="30%" height="1rem" className="mt-2" />
									</div>
								</div>
							</S.LoadingRow>
						))}
					</div>
				) : notificationData?.length === 0 ? (
					<S.NoNotificationMessage>
						<LottiePlayer name="no-notification" width="15rem" height="auto" style={{ margin: 'auto' }} />

						<Typography variant="p2" fontWeight="800" className="pb-4 pt-4" color="var(--black)" noWrap>
							notificationsEmptyTitle
						</Typography>

						<Typography className="pb-4" color="var(--content-2)" noWrap>
							notificationsEmptySubtitle
						</Typography>

						<Button
							variant="blue"
							style={{ margin: 'auto' }}
							label="viewNotificationsPage"
							onClick={() => {
								navigate(PATHS.NOTIFICATIONS);
								closePopper?.();
							}}
						/>
					</S.NoNotificationMessage>
				) : (
					Array.isArray(notificationData) &&
					notificationData.map((notification, index) => {
						return (
							<NotificationDataItem
								key={index}
								notification={notification}
								handleReadNotification={handleReadNotification}
								closePopper={closePopper}
								getLocaleDateWithTime={getLocaleDateWithTime}
								darkMode={darkMode}
							/>
						);
					})
				)}
			</div>
			{notificationData?.length !== 0 && (
				<Button
					variant="blueBorderless"
					css={S.ViewAllNotificationsButtonCss}
					label="viewAllNotifications"
					onClick={() => {
						navigate(PATHS.NOTIFICATIONS);
						closePopper?.();
					}}
				/>
			)}
		</S.NotificationPopup>
	);
};

const NotificationDataItem = ({
	notification,
	closePopper,
	handleReadNotification,
	getLocaleDateWithTime,
	darkMode
}) => {
	const targetRef = useRef(null);

	return (
		<div key={notification.ID}>
			<S.Content
				onClick={() => {
					NiceModal.show(NotificationDetailsModal, {
						selectedNotification: notification
					});
					!notification.isRead && handleReadNotification(notification.ID);
					closePopper?.();
				}}>
				<div className="notification-container" ref={targetRef}>
					<div>
						<S.BoxIcon
							bgColor={
								notificationType.find((item) => item.type.includes(notification.type))?.bgColor ||
								notificationType.find((item) => item.type.includes('default'))?.bgColor
							}>
							<LordIcon
								primaryColor={THEME.BLACK}
								secondaryColor={darkMode ? THEME.DARK.PRIMARY_COLOR : THEME.LIGHT.PRIMARY_COLOR}
								width={'2.6rem'}
								height={'2.6rem'}
								src={
									notificationType.find((item) => item.type.includes(notification.type))?.lordIcon ||
									notificationType.find((item) => item.type.includes('default'))?.lordIcon
								}
								target={targetRef}
							/>
						</S.BoxIcon>
					</div>

					<div>
						<div className="content">
							{notification.content && (
								<Typography
									color="var(--black)"
									style={{ maxWidth: '60rem' }}
									fontWeight="500"
									className="pb-2"
									noWrap>
									{notification.content}
								</Typography>
							)}
							{notification.date && (
								<Typography className="pb-3" color="var(--content-3)" variant="caption" noWrap>
									{getLocaleDateWithTime(notification.date)}
								</Typography>
							)}
						</div>
					</div>
					<div className="pt-2">
						{!notification.isRead && <Badge size="standart" variant="dot" color="red" />}
					</div>
				</div>
			</S.Content>
		</div>
	);
};

export default NotificationPopup;
